import { IApiResponseData, IMetricsEntry } from "@/pages/dashboard/pages/overview/components/OverviewMetricsChart/type";

/** overview date_event_list 单日内的分组块（与 EventDetailsPop 左侧类型列一致） */
export interface IOverviewEventListBlock {
  event_type?: unknown;
  events?: number;
  event_record?: unknown[];
  [key: string]: unknown;
}

export interface IOverviewEventDayRow {
  events?: number;
  date?: string;
  event_list?: IOverviewEventListBlock[];
  [key: string]: unknown;
}

function overviewEventTypeMatchesToken(
  eventType: unknown,
  token: string,
): boolean {
  const want = String(token).trim().toLowerCase();
  if (!want) {
    return false;
  }
  if (eventType && typeof eventType === 'object') {
    const o = eventType as { name_en?: string; name_cn?: string };
    const en = String(o.name_en ?? '').trim().toLowerCase();
    const cn = String(o.name_cn ?? '').trim().toLowerCase();
    if (en === want || cn === want) {
      return true;
    }
  }
  if (typeof eventType === 'string') {
    return String(eventType).trim().toLowerCase() === want;
  }
  return false;
}

/**
 * 将指定类型的事件块排到当日 `event_list` 末尾（其它类型相对顺序不变）。
 */
export function applyEventTypesDisplayLastToOverviewEventData(
  eventData: unknown[] | undefined,
  tailTypes: string[] | undefined,
): unknown[] {
  if (!Array.isArray(eventData) || !tailTypes?.length) {
    return (eventData || []) as unknown[];
  }
  const tails = tailTypes
    .map(t => String(t).trim().toLowerCase())
    .filter(Boolean);
  if (!tails.length) {
    return eventData as unknown[];
  }

  return eventData.map(day => {
    if (!day || typeof day !== 'object' || !Array.isArray((day as IOverviewEventDayRow).event_list)) {
      return day;
    }
    const d = day as IOverviewEventDayRow;
    const list = d.event_list!;
    const head: IOverviewEventListBlock[] = [];
    const tailByIdx: IOverviewEventListBlock[][] = tails.map(() => []);

    list.forEach(block => {
      let matched = -1;
      tails.forEach((tail, idx) => {
        if (overviewEventTypeMatchesToken(block?.event_type, tail)) {
          matched = idx;
        }
      });
      if (matched >= 0) {
        tailByIdx[matched].push(block);
      } else {
        head.push(block);
      }
    });

    const tailFlat = tailByIdx.flat();
    return {
      ...d,
      event_list: [...head, ...tailFlat],
    };
  });
}

export const getAggregateValues = (
  data: IMetricsEntry[],
  metricKey: string,
  typeMap?: { sum?: string; avg?: string; max?: string; min?: string; },
) => {
  const result = { sum: '', avg: '', max: '', min: '' };

  if (!metricKey) return result;

  /** 每个聚合项对应的后端 type，默认使用自身 key */
  const sumType = typeMap?.sum || 'sum';
  const avgType = typeMap?.avg || 'avg';
  const maxType = typeMap?.max || 'max';
  const minType = typeMap?.min || 'min';

  data.forEach(item => {
    const value = item?.metrics?.[metricKey]?.value;
    if (item.type === sumType) result.sum = value ?? '';
    if (item.type === avgType) result.avg = value ?? '';
    if (item.type === maxType) result.max = value ?? '';
    if (item.type === minType) result.min = value ?? '';
  });

  return result;
};

export const processApiData = (rawData: IApiResponseData | IMetricsEntry[]): { list: IMetricsEntry[]; aggregate?: IMetricsEntry[]; dimInfo?: string[]; } => {
  let list: IMetricsEntry[] = [];
  let aggregate: IMetricsEntry[] | undefined = undefined;
  let dimInfo: string[] | undefined = undefined;

  if (
    rawData &&
    typeof rawData === 'object' &&
    !Array.isArray(rawData) && 
    'list' in (rawData)
  ) {
    /** 处理 IApiResponseData 格式 */
    const structured = rawData;
    list = structured.list ?? [];

    if ('aggregate' in structured)
      aggregate = structured?.aggregate;

    if ('dim_info' in structured)
      dimInfo = structured?.dim_info;
  } else {
    /** 处理 IMetricsEntry 格式 */
    list = rawData;
    aggregate = undefined;
    dimInfo = undefined;
  }

  return { list, aggregate, dimInfo };
};
